import { useEffect, useCallback } from 'react';
import webAnalyticsService from './analytics';

// React Hook for Web Analytics
export const useAnalytics = () => {
  // Initialize analytics on component mount
  useEffect(() => {
    const initAnalytics = async () => {
      try {
        await webAnalyticsService.init();
        console.log('✅ Web Analytics initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize web analytics:', error);
      }
    };

    initAnalytics();
  }, []);

  // Driver Analytics
  const logDriverHome = useCallback(() => {
    webAnalyticsService.logDriverHome();
  }, []);

  const logDriverSignup = useCallback((driverData = {}) => {
    webAnalyticsService.logDriverSignup(driverData);
  }, []);

  // Passenger Analytics
  const logPassengerHome = useCallback(() => {
    webAnalyticsService.logPassengerHome();
  }, []);

  const logPassengerSignup = useCallback((passengerData = {}) => {
    webAnalyticsService.logPassengerSignup(passengerData);
  }, []);

  // Ride Analytics
  const logRideRequested = useCallback((rideData = {}) => {
    webAnalyticsService.logRideRequested(rideData);
  }, []);

  const logRideCompleted = useCallback((rideData = {}) => {
    webAnalyticsService.logRideCompleted(rideData);
  }, []);

  const logRideCancelled = useCallback((rideData = {}) => {
    webAnalyticsService.logRideCancelled(rideData);
  }, []);

  // Rating Analytics
  const logDriverRating = useCallback((ratingData = {}) => {
    webAnalyticsService.logDriverRating(ratingData);
  }, []);

  const logPassengerRating = useCallback((ratingData = {}) => {
    webAnalyticsService.logPassengerRating(ratingData);
  }, []);

  // Registration Funnel Analytics
  const logRegisterButtonClick = useCallback((userType, screenName) => {
    webAnalyticsService.logRegisterButtonClick(userType, screenName);
  }, []);

  const logRegisterScreenView = useCallback((userType, screenName) => {
    webAnalyticsService.logRegisterScreenView(userType, screenName);
  }, []);

  // Promo Analytics
  const logPromoApplied = useCallback((promoData = {}) => {
    webAnalyticsService.logPromoApplied(promoData);
  }, []);

  // App Update Analytics
  const logAppUpdate = useCallback((updateData = {}) => {
    webAnalyticsService.logAppUpdate(updateData);
  }, []);

  // Purchase Analytics
  const logPurchase = useCallback((purchaseData = {}) => {
    webAnalyticsService.logPurchase(purchaseData);
  }, []);

  // Custom Events
  const logCustomEvent = useCallback((eventName, parameters = {}) => {
    webAnalyticsService.logCustomEvent(eventName, parameters);
  }, []);

  // Error Tracking
  const logError = useCallback((errorData = {}) => {
    webAnalyticsService.logError(errorData);
  }, []);

  // User Management
  const setUserId = useCallback((userId) => {
    webAnalyticsService.setUserId(userId);
  }, []);

  const setUserProperty = useCallback((name, value) => {
    webAnalyticsService.setUserProperty(name, value);
  }, []);

  // Screen Tracking
  const logScreenView = useCallback((screenName, screenClass) => {
    webAnalyticsService.logScreenView(screenName, screenClass);
  }, []);

  // Page View Tracking (for web)
  const logPageView = useCallback((pageData = {}) => {
    webAnalyticsService.logCustomEvent('page_view', {
      page_title: pageData.title || document.title,
      page_url: pageData.url || window.location.href,
      page_referrer: pageData.referrer || document.referrer,
      user_type: pageData.userType || 'guest',
      timestamp: new Date().toISOString(),
      ...pageData,
    });
  }, []);

  return {
    // Driver Events
    logDriverHome,
    logDriverSignup,
    
    // Passenger Events
    logPassengerHome,
    logPassengerSignup,
    
    // Ride Events
    logRideRequested,
    logRideCompleted,
    logRideCancelled,
    
    // Rating Events
    logDriverRating,
    logPassengerRating,
    
    // Registration Events
    logRegisterButtonClick,
    logRegisterScreenView,
    
    // Promo Events
    logPromoApplied,
    
    // App Events
    logAppUpdate,
    logPurchase,
    
    // Custom Events
    logCustomEvent,
    
    // Error Tracking
    logError,
    
    // User Management
    setUserId,
    setUserProperty,
    
    // Screen/Page Tracking
    logScreenView,
    logPageView,
  };
};

export default useAnalytics;
